import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { motion } from 'framer-motion';
import axios from 'axios';
import './seasonalEffects.css';

// Seasonal themes configuration
const seasonalThemes = {
  winter: {
    name: 'Зима',
    gradient: 'linear-gradient(to bottom, rgba(30, 77, 140, 0.7), rgba(0, 0, 0, 0.8))',
    background: '/assets/seasonal/winter-bg.jpg',
    themeMessage: 'Сибирская Зима',
    overlay: (
      <>
        <Box
          className="snowfall"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
        <Box
          className="frost"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '100%',
            backgroundImage: 'url(/assets/seasonal/frost.png)',
            backgroundSize: 'cover',
            opacity: 0.4,
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
      </>
    ),
    styles: {
      color: 'white',
      textShadow: '0 2px 8px rgba(0,0,0,0.5)',
    }
  },
  spring: {
    name: 'Весна',
    gradient: 'linear-gradient(to bottom, rgba(76, 175, 80, 0.6), rgba(0, 0, 0, 0.7))',
    background: '/assets/seasonal/spring-bg.jpg',
    themeMessage: 'Весна Цветёт',
    overlay: (
      <>
        <Box
          className="spring-leaves"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
            zIndex: 1,
          }}
        >
          <Box className="leaf1" />
          <Box className="leaf2" />
        </Box>

        <Box
          className="butterflies"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
      </>
    ),
    styles: {
      color: 'white',
      textShadow: '0 2px 8px rgba(0,0,0,0.5)',
    }
  },
  summer: {
    name: 'Лето',
    gradient: 'linear-gradient(to bottom, rgba(255, 152, 0, 0.6), rgba(0, 0, 0, 0.7))',
    background: '/assets/seasonal/summer-bg.jpg',
    themeMessage: 'Жаркое Лето',
    overlay: (
      <>
        <Box
          className="sunrays"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(ellipse at top, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0) 70%)',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />

        {/* Summer Elements Animation */}
        <Box
          className="dandelion-seeds"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
            zIndex: 1,
          }}
        >
          <Box className="daisy" />
          <Box className="sun" />
        </Box>
      </>
    ),
    styles: {
      color: 'white',
      textShadow: '0 2px 8px rgba(0,0,0,0.5)',
    }
  },
  autumn: {
    name: 'Осень',
    gradient: 'linear-gradient(to bottom, rgba(230, 81, 0, 0.7), rgba(0, 0, 0, 0.8))',
    background: '/assets/seasonal/autumn-bg.jpg',
    themeMessage: 'Золотая Осень',
    overlay: (
      <>
        <Box
          className="falling-leaves"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
            zIndex: 1,
          }}
        >
          <Box className="leaf1" />
          <Box className="leaf2" />
        </Box>

        <Box
          className="autumn-fog"
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '30%',
            background: 'linear-gradient(to top, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 100%)',
            filter: 'blur(8px)',
            zIndex: 1,
          }}
        />
      </>
    ),
    styles: {
      color: 'white',
      textShadow: '0 2px 8px rgba(0,0,0,0.5)',
    }
  },
  night: {
    name: 'Ночь',
    gradient: 'linear-gradient(to bottom, #0a1128, #000000)',
    background: 'none',
    themeMessage: 'Доброй Ночи!',
    overlay: (
      <>
        <Box
          className="stars"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
        <Box
          className="shooting-stars"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
      </>
    ),
    styles: {
      color: 'white',
      textShadow: '0 2px 8px rgba(0,0,0,0.7)',
    }
  }
};

// Holiday themes
const holidayThemes = {
  newYear: {
    name: 'Новый год',
    gradient: 'linear-gradient(to bottom, rgba(25, 118, 210, 0.7), rgba(0, 0, 0, 0.8))',
    background: '/assets/seasonal/new-year-bg.jpg',
    themeMessage: 'С Новым Годом, Друзья!',
    overlay: (
      <>
        {/* Regular snowfall effect */}
        <Box
          className="snowfall"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
        {/* Heavy snowfall with snowflake images */}
        <Box
          className="snowfall-heavy"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
        <Box
          className="gifts"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
        <Box
          className="christmas-trees"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
        <Box
          className="lights"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '100%',
            backgroundImage: 'url(/assets/seasonal/lights.png)',
            backgroundSize: 'cover',
            opacity: 0.6,
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
      </>
    ),
    styles: {
      color: 'white',
      textShadow: '0 2px 8px rgba(0,0,0,0.5)',
    }
  },
  may9: {
    name: 'День Победы',
    gradient: 'linear-gradient(to bottom, rgba(244, 67, 54, 0.6), rgba(0, 0, 0, 0.8))',
    background: '/assets/seasonal/victory-day-bg.jpg',
    themeMessage: 'С Днём Великой Победы!',
    overlay: (
      <>
        <Box
          className="ribbons"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url(/assets/seasonal/ribbons.png)',
            backgroundSize: 'cover',
            opacity: 0.4,
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
        <Box
          className="victory-star"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
            zIndex: 2,
          }}
        />
      </>
    ),
    styles: {
      color: 'white',
      textShadow: '0 2px 8px rgba(0,0,0,0.5)',
    }
  }
};

// Determine current season based on date
const getCurrentSeason = () => {
  const month = new Date().getMonth();
  
  if (month >= 2 && month <= 4) return 'spring'; // March to May
  if (month >= 5 && month <= 7) return 'summer'; // June to August
  if (month >= 8 && month <= 10) return 'autumn'; // September to November
  return 'winter'; // December to February
};

// Check if current date matches a holiday
const getCurrentHoliday = () => {
  const today = new Date();
  const day = today.getDate();
  const month = today.getMonth();
  
  // New Year period (December 20 - January 15)
  if ((month === 11 && day >= 20) || (month === 0 && day <= 15)) {
    return 'newYear';
  }
  
  // Victory Day (May 1 - May 12)
  if (month === 4 && day >= 1 && day <= 12) {
    return 'may9';
  }
  
  return null;
};

// Initial black theme for smooth transition
const initialBlackTheme = {
  name: 'Initial',
  gradient: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0.95))',
  background: '/assets/seasonal/night-bg.jpg', // Using night background as base
  overlay: null, // No overlay elements for initial theme
  styles: {
    color: 'white',
    textShadow: '0 2px 8px rgba(0,0,0,0.7)',
  }
};

const SeasonalHero = ({ children, customTheme = null, onThemeChange = null }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Состояние для хранения высоты hero-секции
  const [heroHeight, setHeroHeight] = useState(null);
  
  const [initialTheme] = useState(initialBlackTheme); // Initial black theme never changes
  const [targetTheme, setTargetTheme] = useState(null); // Target theme to transition to
  const [isLoading, setIsLoading] = useState(true);
  const [transitionActive, setTransitionActive] = useState(false); // Track transition state
  

  // Создаем реф для отслеживания первой загрузки
  const isFirstLoad = useRef(true);

  // Используем useLayoutEffect для вычисления высоты до отрисовки
  useLayoutEffect(() => {
    // Вычисляем высоту только при первой загрузке
    if (isFirstLoad.current) {
      // Используем visualViewport для более точного определения высоты на мобильных
      const viewportHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      
      // Вычисляем высоту для hero-секции
      let calculatedHeight;
      
      if (isSmall) {
        // Для маленьких экранов (мобильные)
        calculatedHeight = viewportHeight;
      } else if (isMobile) {
        // Для планшетов
        calculatedHeight = viewportHeight;
      } else {
        // Для десктопов
        calculatedHeight = viewportHeight;
      }
      
      // Устанавливаем высоту
      setHeroHeight(calculatedHeight);
      
      // Отмечаем, что первая загрузка завершена
      isFirstLoad.current = false;
      
      // Дополнительная проверка через небольшой промежуток времени
      // для учета возможных изменений после полной загрузки страницы
      setTimeout(() => {
        const updatedViewportHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
        if (Math.abs(updatedViewportHeight - calculatedHeight) > 20) { // Если разница больше 20px
          setHeroHeight(updatedViewportHeight);
        }
      }, 500);
    }
  }, [isSmall, isMobile]);
  
  useEffect(() => {
    // Флаг для предотвращения обновления состояния после размонтирования компонента
    let isMounted = true;
    
    const determineTheme = async () => {
      // Всегда определяем тему при первой загрузке
      // Если передана кастомная тема, используем её
      
      let finalTheme = initialBlackTheme;
      
      try {
        // If a custom theme is provided, use it
        if (customTheme) {
          finalTheme = customTheme;
        } else {
          // Try to get hero theme setting from API
          const response = await axios.get('/api/settings');
          
          // Проверяем, что компонент все еще смонтирован
          if (!isMounted) return;
          
          const data = response.data || {};
          
          // If there's a manually set theme in settings and it's not 'auto', use it
          if (data.heroTheme && data.heroTheme !== 'auto') {
            if (seasonalThemes[data.heroTheme]) {
              finalTheme = seasonalThemes[data.heroTheme];
            } else if (holidayThemes[data.heroTheme]) {
              finalTheme = holidayThemes[data.heroTheme];
            }
          } else {
            // Use auto detection (default or explicitly set to 'auto')
            // First check for holidays
            const holiday = getCurrentHoliday();
            if (holiday && holidayThemes[holiday]) {
              console.log('Using holiday theme:', holiday);
              finalTheme = holidayThemes[holiday];
            } else {
              // Then fall back to seasonal
              const season = getCurrentSeason();
              console.log('Using seasonal theme:', season);
              finalTheme = seasonalThemes[season];
            }
          }
        }
        
        // Wait a short delay before starting the transition
        setTimeout(() => {
          setTargetTheme(finalTheme);
          // Start the transition after setting the target theme
          setTimeout(() => {
            setTransitionActive(true);
            
            // Call onThemeChange callback if provided
            if (finalTheme && finalTheme.themeMessage && onThemeChange) {
              onThemeChange(finalTheme.themeMessage);
            }
          }, 100); // Small delay to ensure the target theme is set
        }, 1000); // 1 second delay before starting transition
        
      } catch (error) {
        console.error('Error fetching hero settings:', error);
        // Fallback to current season on error
        const season = getCurrentSeason();
        setTimeout(() => {
          setTargetTheme(seasonalThemes[season]);
          setTimeout(() => {
            setTransitionActive(true);
          }, 100);
        }, 1000);
      } finally {
        setIsLoading(false);
      }
    };
    
    determineTheme();
    
    // Add event listener for theme updates
    const handleThemeUpdate = (event) => {
      if (event.detail && event.detail.heroTheme) {
        const newTheme = event.detail.heroTheme;
        // Reset transition state
        setTransitionActive(false);
        
        // Set new target theme
        setTimeout(() => {
          if (seasonalThemes[newTheme]) {
            setTargetTheme(seasonalThemes[newTheme]);
          } else if (holidayThemes[newTheme]) {
            setTargetTheme(holidayThemes[newTheme]);
          }
          
          // Start new transition
          setTimeout(() => {
            setTransitionActive(true);
          }, 100);
        }, 500); // Small delay to ensure transition reset
      }
    };
    
    window.addEventListener('settingsUpdated', handleThemeUpdate);
    
    return () => {
      window.removeEventListener('settingsUpdated', handleThemeUpdate);
    };
  }, [customTheme]);
  
  // Render both initial black theme and target theme with transition
  return (
    <Box
      sx={{
        position: 'relative',
        // Используем вычисленную высоту или значение по умолчанию
        height: heroHeight ? `${heroHeight}px` : (isMobile ? '100vh' : '100vh'),
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        mb: 6,
        mt: 0, // Убираем отступ сверху
        pt: 0, // Убираем внутренний отступ сверху
        color: targetTheme ? targetTheme.styles.color : initialTheme.styles.color,
        textShadow: targetTheme ? targetTheme.styles.textShadow : initialTheme.styles.textShadow,
        // Белый переход удален
        backgroundColor: '#000', // Добавляем черный фон для плавного перехода
        top: 0, // Позиционируем от верха страницы
      }}
    >
      {/* Initial black theme layer - always visible but fades out */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `${initialTheme.gradient}`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          opacity: transitionActive ? 0 : 1,
          transition: 'opacity 3s ease-in-out', // Longer transition for smoother fade
          zIndex: 1
        }}
      />
      
      {/* Target theme layer - fades in */}
      {targetTheme && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `${targetTheme.gradient}`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
            opacity: transitionActive ? 1 : 0,
            transition: 'opacity 3s ease-in-out', // Longer transition for smoother fade
            zIndex: 2
          }}
        />
      )}
      
      {/* Animated overlay elements - only show for target theme with transition */}
      {targetTheme && targetTheme.overlay && (
        <Box 
          sx={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: transitionActive ? 1 : 0,
            transition: 'opacity 3s ease-in-out', // Match the background transition
            zIndex: 3,
            pointerEvents: 'none'
          }}
        >
          {targetTheme.overlay}
        </Box>
      )}
      
      {/* Theme message removed */}
      
      {/* Main content */}
      <Box sx={{ position: 'relative', zIndex: 2, width: '100%' }}>
        {children}
      </Box>
    </Box>
  );
};

export default SeasonalHero;
